import { useEffect, useRef, useState } from 'react';
import ImageLoader from '../../../components/ImageLoader';
import { StarIcon } from '@heroicons/react/24/solid';
import { authHeaders, axiosInstance, encodeValue, formatRupiah, isEmptyData, sendAnalyticsCustomEvent, sendAnalyticsEvent, trackMetaPixelEvent } from '../../../utils/helper';
import Cart from '../Cart';
import { useMemo } from 'react';
import { deliveryId, uniqueID } from '../../../utils/constants';
import { useDispatch, useSelector } from 'react-redux';
import { BottomSheet } from 'react-spring-bottom-sheet';
import { clearDetail, handleGetProductDetail, setDetail } from '../../../store/reducers/product';
import qs from 'qs';
import { handleGetCartDetail } from '../../../store/reducers/cart';
import { autoRegister, setUser } from '../../../store/reducers/user';
import ProductCombo from './Combo';
import ProductSimple from './Simple';

const ProductName = ({ name, longestName }) => {
	const nameRef = useRef(null);
	const [maxWidth, setMaxWidth] = useState(0);
	const [isTwoLines, setIsTwoLines] = useState(false);
	const canvasRef = useRef(document.createElement('canvas'));

	useEffect(() => {
		if (!nameRef?.current) return;
		const resizeObserver = new ResizeObserver(() => {
			setMaxWidth(nameRef?.current?.offsetWidth);
		});

		resizeObserver?.observe(nameRef?.current);

		return () => resizeObserver?.disconnect();
	}, []);

	useEffect(() => {
		if (!maxWidth) return;
		const context = canvasRef?.current.getContext('2d');
		const textWidth = context?.measureText(longestName || name).width;

		setIsTwoLines(textWidth > maxWidth);
	}, [name, longestName, maxWidth]);

	return (
		<div ref={nameRef} className='flex flex-row'>
			<div className={`text-gray-800 text-xs font-semibold ${isTwoLines ? 'line-clamp-2 min-h-[2rem]' : ''}`}>{name}</div>
		</div>
	);
};

const ProductDetail = ({ name, longestName }) => {
	const nameRef = useRef(null);
	const [maxWidth, setMaxWidth] = useState(0);
	const [isTwoLines, setIsTwoLines] = useState(false);
	const canvasRef = useRef(document.createElement('canvas'));

	useEffect(() => {
		if (!nameRef?.current) return;
		const resizeObserver = new ResizeObserver(() => {
			setMaxWidth(nameRef?.current?.offsetWidth);
		});

		resizeObserver?.observe(nameRef?.current);

		return () => resizeObserver?.disconnect();
	}, []);

	useEffect(() => {
		if (!maxWidth) return;
		const context = canvasRef?.current.getContext('2d');
		const textWidth = context?.measureText(longestName || name).width;

		setIsTwoLines(textWidth > maxWidth);
	}, [name, longestName, maxWidth]);

	return (
		<div ref={nameRef} className='flex flex-row'>
			<div className={`text-gray-700 text-xxs font-medium ${isTwoLines ? 'line-clamp-2 min-h-[2rem]' : ''}`}>{name}</div>
		</div>
	);
};

const DetailProduct = (props) => {
	const dispatch = useDispatch();

	const { detail } = useSelector((store) => store?.product);
	const { cart } = useSelector((store) => store?.cart);
	const { details: outlet } = useSelector((store) => store?.outlet);
	const user = useSelector((store) => store?.user);
	const company = useSelector((store) => store?.company);
	const device = useSelector((store) => store?.device);
	const { selectedOrderType } = useSelector((store) => store?.order);

	const handleCloseBottomSheet = () => {
		dispatch(clearDetail());
	};

	const productCombo = detail?.product_type === '2';

	const handleGetCart = async (userData) => {
		dispatch(
			handleGetCartDetail({
				customerID: encodeValue(userData?.customerID || user?.customerID),
				companyID: encodeValue(userData?.companyID || user?.companyID),
				storeID: encodeValue(outlet?.storeID),
				availabilityID: selectedOrderType?.av_id
			}),
		);
	};

	const handleAutoRegister = async () => {
		if (isEmptyData(user?.customerID) && !isEmptyData(company?.companyID)) {
			const result = await dispatch(
				autoRegister({
					companyId: Number(company?.companyID),
					deviceId: device?.device_id,
				}),
			).unwrap();

			if (isEmptyData(result)) {
				console.log('please login');
				return;
			}

			const userData = {
				customerID: result?.customerId,
				companyID: result?.customerCompanyId,
				is_b2b: true,
			};

			await Promise.resolve(
				dispatch(
					setUser({
						...userData,
					}),
				),
			);
			return userData;
		}
		return user;
	};

	const handleCart = async (data) => {
		try {
			const userData = await handleAutoRegister();
			let postData = {
				unquieid: uniqueID,
				availabilityID: selectedOrderType?.av_id || deliveryId,
				availabilityName: selectedOrderType?.av_name || 'Delivery',
				shopID: encodeValue(outlet?.storeID),
				customerID: encodeValue(userData?.customerID),
				companyID: encodeValue(userData?.companyID),
			};
			if (data?.productType === 'Simple') {
				postData = {
					...postData,
					...data,
				};
			} else {
				postData = {
					...postData,
					...data,
					comboset: JSON.stringify(data?.comboset),
				};
			}

			sendAnalyticsCustomEvent('add_to_cart', {
				customer_id: userData?.customerID,
				value: postData?.productTotalPrice || postData?.price || 0,
				merchants_id: outlet?.storeID,
				items: postData,
			});
			trackMetaPixelEvent('add_to_cart', {
				customer_id: userData?.customerID,
				value: postData?.productTotalPrice || postData?.price || 0,
				merchants_id: outlet?.storeID,
				items: postData,
			});

			const url = `cart/${data?.type === 'add' ? 'createCart' : 'updateCartItem'}`;
			const res = await axiosInstance.post(url, qs.stringify(postData), authHeaders());
			if (res?.data?.status === 'ok') {
				if (data?.productType === 'Simple' || (data?.productType === "Combo" && data?.type === "add")) {
          handleCloseBottomSheet(); 
        }
				await handleGetCart(userData);
			}
		} catch (error) {
			console.log('error: ', error);
		}
	};

	const productProps = {
		product: detail,
		cart,
		handleCart,
		setCartItem: null,
	};

	return (
		<BottomSheet initialFocusRef={false} key={'detail-product'} open={!isEmptyData(detail)} onDismiss={handleCloseBottomSheet}>
			{productCombo ? <ProductCombo {...productProps} /> : <ProductSimple {...productProps} />}
		</BottomSheet>
	);
};

const BigProduct = (props) => {
	const { product = null, gps } = props;

	const dispatch = useDispatch();
	const user = useSelector((store) => store?.user);
	const { details: outlet } = useSelector((store) => store?.outlet);

	const isInactive = !product?.product_status || product?.product_status === 'I';

	const groupedColumns = useMemo(() => {
		if (!product?.length) return [];

		return Array.from({ length: Math.ceil(product.length / 2) }, (_, i) => {
			const group = [i * 2, i * 2 + 1].filter((idx) => idx < product.length);
			const names = group?.map((idx) => product?.[idx]?.product_name);
			const longest_name = names?.reduce((longest, name) => (name?.length > longest?.length ? name : longest), '');
			return { group, names, longest_name };
		});
	}, [product]);

	const rows = (idx) => {
		const foundRow = groupedColumns?.find((group) => group.group.includes(idx));
		return foundRow ? foundRow : {};
	};

	const row = rows(product?.id);

	const handleGetDetail = async (product) => {
		if (!product?.product_id) return;

		gps?.checkGps(() => {
			dispatch(setDetail(product));
		});
	};

	return (
		<>
			<div
				id={product?.id}
				key={product?.id}
				className={`h-full w-full !z-0 ${isInactive && 'opacity-60'}`}
				style={{ cursor: isInactive ? 'not-allowed' : 'pointer' }}
				onClick={() => {
					if (isInactive) return;
					sendAnalyticsEvent('Click Product', 'merchant_add_product', product?.product_name);
					handleGetDetail(product);
				}}>
				<div className='aspect-[1/1] relative w-full h-auto'>
					<ImageLoader alt={product?.product_thumbnail} src={product?.product_thumbnail} style={isInactive ? { filter: 'grayscale(100%)', opacity: 0.5 } : {}} />
					<Cart {...{ product }} />
				</div>
				<div className={`flex gap-1 items-center pt-2 ${isInactive ? 'opacity-50' : ''}`}>
					<StarIcon className={`h-4 w-4 ${isInactive ? 'text-gray-400' : 'text-star'}`} />
					<div className={`text-xs ${isInactive ? 'text-gray-400' : 'text-black'}`}>{product?.rating ?? 0}</div>
					<div className={`text-xs ${isInactive ? 'text-gray-400' : 'text-gray-900'}`}>{`(${product?.totalRating ?? 0})`}</div>
				</div>
				<div className={`mt-1 ${isInactive ? 'opacity-50' : ''}`}>
					<ProductName name={product?.product_name} longestName={row?.longest_name} disabled={isInactive} />
				</div>
				<div className={`text-xs flex items-center pt-1 ${isInactive ? 'opacity-50' : ''}`}>
					<div className={`font-semibold pr-1 ${isInactive ? 'text-gray-400' : 'text-gray-800'}`}>{formatRupiah(product?.product_price)}</div>
					{Number(product?.product_cost || 0) > 0 && <div className={`line-through ${isInactive ? 'text-gray-400' : 'text-gray-500'}`}>{formatRupiah(product?.product_cost)}</div>}
				</div>
			</div>
		</>
	);
};

const SmallProduct = (props) => {
	const { product = null, gps } = props;

	const dispatch = useDispatch();
	const user = useSelector((store) => store?.user);
	const { details: outlet } = useSelector((store) => store?.outlet);

	const isInactive = !product?.product_status || product?.product_status === 'I';

	const groupedColumns = useMemo(() => {
		if (!product?.length) return [];

		return Array.from({ length: Math.ceil(product.length / 2) }, (_, i) => {
			const group = [i * 2, i * 2 + 1].filter((idx) => idx < product.length);
			const names = group?.map((idx) => product?.[idx]?.product_name);
			const longest_name = names?.reduce((longest, name) => (name?.length > longest?.length ? name : longest), '');
			return { group, names, longest_name };
		});
	}, [product]);

	const rows = (idx) => {
		const foundRow = groupedColumns?.find((group) => group.group.includes(idx));
		return foundRow ? foundRow : {};
	};

	const row = rows(product?.id);

	const handleGetDetail = async (product) => {
		console.log('handleGetDetail');
		if (!product?.product_id) return;

		gps?.checkGps(() => {
      dispatch(setDetail(product));
		});
	};

	return (
		<>
			<div
				id={product?.id}
				key={product?.id}
				className={`relative h-full w-full rounded-md p-2 border-1 border-solid border-gray-100 !z-0 flex shadow-sm ${isInactive && 'opacity-60'}`}
				style={{ cursor: isInactive ? 'not-allowed' : 'pointer' }}
				onClick={() => {
					if (isInactive) return;
					sendAnalyticsEvent('Click Product', 'merchant_add_product', product?.product_name);
					handleGetDetail(product);
				}}>
				<div className='aspect-[1/1] relative w-20 h-auto'>
					<ImageLoader alt={product?.product_thumbnail} src={product?.product_thumbnail} style={isInactive ? { filter: 'grayscale(100%)', opacity: 0.5 } : {}} />
				</div>
				<div className={`ml-2 flex flex-col gap-0.5 ${isInactive ? 'opacity-50' : ''}`}>
					<ProductName name={product?.product_name} longestName={row?.longest_name} disabled={isInactive} />
					<ProductDetail name={product?.detail} disabled={isInactive} />
					<div className={`flex gap-1 ${isInactive ? 'opacity-50' : ''}`}>
						<StarIcon className={`h-3.5 w-auto ${isInactive ? 'text-gray-400' : 'text-star'}`} />
						<div className={`text-xxs ${isInactive ? 'text-gray-400' : 'text-black'}`}>{product?.rating ?? 0}</div>
						<div className={`text-xxs ${isInactive ? 'text-gray-400' : 'text-gray-900'}`}>{`(${product?.totalRating ?? 0})`}</div>
					</div>
					<div className={`text-xs flex ${isInactive ? 'opacity-50' : ''}`}>
						<div className={`font-semibold pr-1 ${isInactive ? 'text-gray-400' : 'text-gray-800'}`}>{formatRupiah(product?.product_price)}</div>
						{Number(product?.product_cost || 0) > 0 && <div className={`line-through ${isInactive ? 'text-gray-400' : 'text-gray-500'}`}>{formatRupiah(product?.product_cost)}</div>}
					</div>
				</div>
				<Cart {...{ product, small: true }} />
			</div>
		</>
	);
};

export { BigProduct, SmallProduct, DetailProduct };
